import { Box, Button, Stack, Text, Table, Badge, Anchor, Center } from '@mantine/core';
import { useState, useEffect } from 'react';
import { useCustom } from '@refinedev/core';
import type { CalendarEvent } from 'src/meetings/models';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import PageLoader from 'src/UI/loader/PageLoader';

const LOADING_MESSAGES = [
  'Reviewing meeting...',
  'Checking compliance...',
  'Organising results...',
  'Analyzing transcript...',
];

type Props = {
  event: CalendarEvent;
};

type ComplianceCheckResult = 'PASSED' | 'FAILED' | 'NOT_APPLICABLE';

type ComplianceCheck = {
  id: number;
  title: string;
  description: string;
  instruction: string;
  complianceRequirement: string;
  result: ComplianceCheckResult | null;
  resultExplanation: string | null;
  urlToCompliance: string | null;
};

type ComplianceChecksResponse = {
  complianceChecks: ComplianceCheck[];
};

const getResultColor = (result: ComplianceCheckResult | null): string => {
  switch (result) {
    case 'PASSED':
      return 'green';
    case 'FAILED':
      return 'red';
    case 'NOT_APPLICABLE':
      return 'gray';
    default:
      return 'gray';
  }
};

const getResultLabel = (result: ComplianceCheckResult | null): string => {
  switch (result) {
    case 'PASSED':
      return 'Satisfied';
    case 'FAILED':
      return 'Not Satisfied';
    case 'NOT_APPLICABLE':
      return 'Not Applicable';
    default:
      return 'Unknown';
  }
};

const getRowBackgroundColor = (result: ComplianceCheckResult | null): string => {
  switch (result) {
    case 'PASSED':
      return 'var(--mantine-color-green-0)';
    case 'FAILED':
      return 'var(--mantine-color-red-0)';
    case 'NOT_APPLICABLE':
      return 'var(--mantine-color-gray-0)';
    default:
      return 'transparent';
  }
};

export const ComplianceTab = ({ event }: Props) => {
  const [hasRun, setHasRun] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);

  const { refetch, data, isLoading, isError, error } = useCustom<ComplianceChecksResponse>({
    url: `calendar-events/${event.id}/check-compliance`,
    method: 'post',
    queryOptions: {
      enabled: false,
    },
  });

  useEffect(() => {
    if (hasRun && isLoading) {
      const interval = setInterval(() => {
        setLoadingMessageIndex(prev => (prev + 1) % LOADING_MESSAGES.length);
      }, 2000);

      return () => clearInterval(interval);
    }
  }, [hasRun, isLoading]);

  const handleRunComplianceCheck = async () => {
    setHasRun(true);
    setLoadingMessageIndex(0);
    await refetch();
  };

  const toggleRow = (id: number) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  if (!event.hasTranscription) {
    return (
      <Box p="md">
        <Text size="sm" c="gray.7">
          There is no recording for this meeting.
        </Text>
      </Box>
    );
  }

  if (hasRun && isLoading) {
    return (
      <Box style={{ position: 'relative', minHeight: '400px' }}>
        <PageLoader absolute={false} />
        <Center mt="xl">
          <Text size="sm" c="gray.7" fw={FONT_WEIGHTS.semibold}>
            {LOADING_MESSAGES[loadingMessageIndex]}
          </Text>
        </Center>
      </Box>
    );
  }

  if (!hasRun && !data) {
    return (
      <Stack gap="md" align="center" mt="xl">
        <Text size="sm" c="gray.7">
          Run a compliance check to review this meeting transcript against regulatory requirements.
        </Text>
        <Button onClick={handleRunComplianceCheck}>Run compliance check</Button>
      </Stack>
    );
  }

  if (isError) {
    const errorMessage = error?.message || 'An error occurred while running the compliance check.';
    const isNoRecordingError = errorMessage.includes('No recording found');

    return (
      <Box p="md">
        <Text size="sm" c="red.7" fw={FONT_WEIGHTS.semibold} mb="xs">
          {isNoRecordingError ? 'No Recording Available' : 'Error'}
        </Text>
        <Text size="sm" c="gray.7">
          {isNoRecordingError
            ? 'There is no recording for this meeting.'
            : 'An error occurred while running the compliance check. Please try again later.'}
        </Text>
      </Box>
    );
  }

  if (!data?.data?.complianceChecks || data.data.complianceChecks.length === 0) {
    return (
      <Box p="md">
        <Text size="sm" c="gray.7">
          No compliance checks available.
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Stack gap="md" mb="lg">
        <Text size="sm" fw={FONT_WEIGHTS.semibold}>
          Compliance Check Results
        </Text>
        <Button onClick={handleRunComplianceCheck} loading={isLoading} variant="light" size="sm">
          Re-run compliance check
        </Button>
      </Stack>

      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={40}></Table.Th>
            <Table.Th>Compliance area</Table.Th>
            <Table.Th>Requirement</Table.Th>
            <Table.Th w={150}>Result</Table.Th>
            <Table.Th>Explanation</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {data.data.complianceChecks.map(check => {
            const isExpanded = expandedRows.has(check.id);
            return (
              <>
                <Table.Tr key={check.id} bg={getRowBackgroundColor(check.result)}>
                  <Table.Td w={40}>
                    <Button
                      variant="subtle"
                      size="xs"
                      p={0}
                      onClick={() => toggleRow(check.id)}
                      style={{ minWidth: 'auto' }}
                    >
                      {isExpanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
                    </Button>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={FONT_WEIGHTS.semibold}>
                      {check.title}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {check.urlToCompliance ? (
                      <Anchor
                        href={check.urlToCompliance}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="sm"
                      >
                        {check.complianceRequirement}
                      </Anchor>
                    ) : (
                      <Text size="sm">{check.complianceRequirement}</Text>
                    )}
                  </Table.Td>
                  <Table.Td w={150}>
                    <Badge color={getResultColor(check.result)} size="sm">
                      {getResultLabel(check.result)}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{check.resultExplanation || '-'}</Text>
                  </Table.Td>
                </Table.Tr>
                {isExpanded && (
                  <Table.Tr key={`${check.id}-detail`} bg={getRowBackgroundColor(check.result)}>
                    <Table.Td colSpan={5} style={{ paddingTop: 0 }}>
                      <Box pl="xl" pb="xs">
                        <Text size="sm" c="gray.7" fw={FONT_WEIGHTS.semibold} mb="xs">
                          Detail:
                        </Text>
                        <Text size="sm" c="gray.8">
                          {check.description}
                        </Text>
                      </Box>
                    </Table.Td>
                  </Table.Tr>
                )}
              </>
            );
          })}
        </Table.Tbody>
      </Table>
    </Box>
  );
};
